import React, { useRef, useState } from 'react';
import { IDocument } from '../Interfaces/IDocument';
import {
  useGetInvoiceFullobjectById,
  useImportDatevCSv,
  useUpdateInvoiceFullObject,
} from '../hooks/reactQuerys/InvoiceQueries';
import { LoadingSpinner } from '../core/LoadingSpinner';
import { useSelector } from 'react-redux';
import { State } from '../redux/mainReducer';
import { fileToBase64 } from '../Document/DocumentOverview';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  IInvoice,
  IInvoiceFullObject,
  invoiceFullSchema,
} from '../Interfaces/IInvoice';
import { IInvoicePaid } from '../Interfaces/IInvoicePaid';
import { InvoiceEdit } from './InvoiceEdit';
import { IinvoiceState } from '../Interfaces/IInvoiceState';
import { ILegalClient } from '../Interfaces/ILegalClient';
import { ILegalTransactionType } from '../Interfaces/ILegalTransactionType';
import { useAlert } from '../context/AlertContext';

interface IProps {
  invoices: IInvoice[];
  invoiceStates: IinvoiceState[];
  legalClients: ILegalClient[];
  legalTransactionTypes: ILegalTransactionType[];
  refetchTrigger: Function;
}

export const InvoiceCsvImportDialog: React.FC<IProps> = ({
  invoices,
  invoiceStates,
  legalClients,
  legalTransactionTypes,
  refetchTrigger,
}) => {
  const { showAlert } = useAlert();
  const inputUpload = useRef<HTMLInputElement | null>(null);

  const mimeTypeArray = useSelector(
    (state: State) => state.mimeTypeArray.storeAcademicTitle
  );

  const { importDatevCsv, isPendingImportDatevCsv } = useImportDatevCSv();
  const [isOpenDialog, setIsOpenDialog] = useState(false);
  const [isSelectedMode, setIsSelectedMode] = useState(true);

  const [newPaids, setNewPaids] = useState<IInvoicePaid[]>([]);
  const [selectedNewPaids, setSelectedNewPaids] = useState<number[]>([]);
  const uniqueIdInvoice = newPaids
    .map(paid => paid.idInvoice)
    .filter((item, i, ar) => ar.indexOf(item) === i);
  //
  const [editInvoiceFullObject, setEditInvoiceFullObject] =
    useState<IInvoiceFullObject | null>(null);
  const [currentInvoiceIdx, setCurrentInvoiceIdx] = useState(0);
  const { getInvoiceFullobjectById, isPendingGetInvoiceFullobjectById } =
    useGetInvoiceFullobjectById();
  const { isPendingUpdateInvoiceFullObject, updateInvoiceFullObject } =
    useUpdateInvoiceFullObject();

  const isPending =
    isPendingGetInvoiceFullobjectById || isPendingUpdateInvoiceFullObject;

  const handleFilesUpload = async (acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      const currentFile = acceptedFiles[0];
      const fileName: string = String(currentFile['name']);

      const uploadedFile: IDocument = {
        idDocument: -1,
        FileName: fileName,
        idMIMEType: 1,
        Data: await fileToBase64(currentFile),
        Created_at: new Date().toISOString(),
        isFromTemplate: false,
        idDocumentDirectory: -1,
        idLegalTransaction: -1,
        perEmail: false,
      };

      try {
        const postedObject = await importDatevCsv(uploadedFile);
        setNewPaids(postedObject);
        setSelectedNewPaids([...postedObject.map(x => x.idInvoicePaid)]);
        setIsOpenDialog(true);
      } catch (error) {
        console.error(error);
        showAlert({
          severity: 'error',
          text: 'Die DATEV-CSV konnte nicht erkannt werden!',
        });
      }
    }
  };

  const handleNormalUpload = (files: null | FileList) => {
    if (files) {
      const acceptedFiles: File[] = Array.from(files);
      handleFilesUpload(acceptedFiles);
    }
    setSelectedNewPaids([]);
    setCurrentInvoiceIdx(0);
    setIsSelectedMode(true);
  };

  const handleStartIteration = async () => {
    const filteredPaids = newPaids.filter(paid =>
      selectedNewPaids.includes(paid.idInvoicePaid)
    );
    const tmpList = filteredPaids
      .map(paid => paid.idInvoice)
      .filter((item, i, ar) => ar.indexOf(item) === i);
    setNewPaids(filteredPaids);
    setIsSelectedMode(false);

    if (tmpList.length > 0) {
      const gettedObject = await getInvoiceFullobjectById(tmpList[0]);
      gettedObject.InvoicePaids = [
        ...gettedObject.InvoicePaids,
        ...filteredPaids.filter(paid => paid.idInvoice === tmpList[0]),
      ];
      setEditInvoiceFullObject(gettedObject);
    }
  };

  const loadNextInvoice = async () => {
    if (uniqueIdInvoice.length > currentInvoiceIdx + 1) {
      setEditInvoiceFullObject(null);
      const currentId = uniqueIdInvoice[currentInvoiceIdx + 1];
      setCurrentInvoiceIdx(currentInvoiceIdx + 1);
      const gettedObject = await getInvoiceFullobjectById(currentId);
      gettedObject.InvoicePaids = [
        ...gettedObject.InvoicePaids,
        ...newPaids.filter(paid => paid.idInvoice === currentId),
      ];
      setEditInvoiceFullObject(gettedObject);
    } else {
      refetchTrigger();
      setIsOpenDialog(false);
    }
  };

  const saveAndNext = async () => {
    if (editInvoiceFullObject !== null) {
      try {
        let checkData = editInvoiceFullObject;
        checkData.LegalTransactionLegalClients = [];
        checkData = invoiceFullSchema.parse(editInvoiceFullObject);
        await updateInvoiceFullObject(checkData);
        showAlert({
          severity: 'success',
          text: `Die Rechnung ${editInvoiceFullObject.InvoiceNumber} wurde erfolgreich gespeichert!`,
        });
      } catch (error) {
        console.error(error);
        showAlert({
          severity: 'error',
          text: `Die Rechnung ${editInvoiceFullObject.InvoiceNumber} konnte nicht gespeichert werden`,
        });
      }
      loadNextInvoice();
    }
  };

  if (isPendingImportDatevCsv) {
    return (
      <Dialog open>
        <DialogContent>
          <LoadingSpinner height='500' />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
      <input
        hidden
        ref={inputUpload}
        type='file'
        accept={'.csv'}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
          handleNormalUpload(event.target.files)
        }
      />
      <Button
        variant='outlined'
        sx={{ float: 'right' }}
        onClick={() => inputUpload.current?.click()}
      >
        DATEV-CSV Abgleich
      </Button>

      <Dialog
        open={isOpenDialog}
        maxWidth={isSelectedMode ? 'xs' : 'lg'}
        fullWidth
      >
        <DialogTitle>Stappelverarbeitung der DATEV-CSV</DialogTitle>
        {isSelectedMode ? (
          <>
            <DialogContent>
              <Table size='small'>
                <TableHead>
                  <TableCell width={10}></TableCell>
                  <TableCell>Zahlungseingang</TableCell>
                </TableHead>
                <TableBody>
                  {uniqueIdInvoice.map(idInvoice => {
                    const currentInvoice = invoices.find(
                      inv => inv.idInvoice === idInvoice
                    );

                    if (currentInvoice === undefined) {
                      return (
                        <>
                          <TableRow>
                            <TableCell colSpan={5}>ERROR</TableCell>
                          </TableRow>
                        </>
                      );
                    } else {
                      return (
                        <>
                          <TableRow>
                            <TableCell colSpan={2}>
                              Rechnung {currentInvoice.InvoiceNumber} mit Wert
                              von{' '}
                              {currentInvoice.Total?.toLocaleString('de-DE', {
                                style: 'currency',
                                currency: 'EUR',
                              })}
                            </TableCell>
                          </TableRow>
                          {newPaids
                            .filter(paid => paid.idInvoice === idInvoice)
                            .map(paid => (
                              <TableRow>
                                <TableCell>
                                  <Checkbox
                                    size='small'
                                    checked={selectedNewPaids.includes(
                                      paid.idInvoicePaid
                                    )}
                                    onChange={() =>
                                      selectedNewPaids.includes(
                                        paid.idInvoicePaid
                                      )
                                        ? setSelectedNewPaids([
                                            ...selectedNewPaids.filter(
                                              sel => sel !== paid.idInvoicePaid
                                            ),
                                          ])
                                        : setSelectedNewPaids([
                                            ...selectedNewPaids,
                                            paid.idInvoicePaid,
                                          ])
                                    }
                                  />
                                </TableCell>
                                <TableCell>
                                  {paid.PaidValue.toLocaleString('de-DE', {
                                    style: 'currency',
                                    currency: 'EUR',
                                  })}
                                </TableCell>
                              </TableRow>
                            ))}
                        </>
                      );
                    }
                  })}
                </TableBody>
              </Table>
            </DialogContent>
            <DialogActions>
              <Button variant='outlined' onClick={() => setIsOpenDialog(false)}>
                Abbruch
              </Button>
              <Button
                variant='contained'
                disabled={selectedNewPaids.length === 0}
                onClick={handleStartIteration}
              >
                Stappelverarbeitung
              </Button>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogContent>
              <Grid container spacing={2}>
                <Grid
                  item
                  xs={3}
                  sx={{
                    position: 'sticky',
                    top: 0,
                    alignSelf: 'flex-start',
                    maxHeight: '100vh',
                    overflowY: 'auto',
                    pr: 2,
                  }}
                >
                  <Stepper
                    activeStep={currentInvoiceIdx}
                    orientation='vertical'
                  >
                    {uniqueIdInvoice.map((idInvoice, idx) => (
                      <Step
                        key={`step-invoice-${idx}`}
                        completed={idx < currentInvoiceIdx}
                      >
                        <StepLabel>
                          {
                            invoices.find(inv => inv.idInvoice === idInvoice)
                              ?.InvoiceNumber
                          }
                        </StepLabel>
                      </Step>
                    ))}
                  </Stepper>
                </Grid>

                <Grid item xs={9}>
                  {isPendingGetInvoiceFullobjectById ||
                  editInvoiceFullObject === null ? (
                    <LoadingSpinner />
                  ) : (
                    <InvoiceEdit
                      key={`edit-stappel-${currentInvoiceIdx}-${editInvoiceFullObject.idInvoice}`}
                      editInvoice={editInvoiceFullObject}
                      setEditInvoice={setEditInvoiceFullObject}
                      invoiceStates={invoiceStates}
                      legalClients={legalClients}
                      legalTransactionTypes={legalTransactionTypes}
                      markNewPaids
                    />
                  )}
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button
                variant='outlined'
                onClick={loadNextInvoice}
                disabled={isPending}
              >
                Weiter ohne zu Speichern
              </Button>
              <Button
                variant='contained'
                disabled={isPending}
                onClick={saveAndNext}
              >
                Weiter mit Speichern
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
};
